import { Controller } from "@hotwired/stimulus"

// Handles direct-to-S3 upload for large files (e.g. 200MB PSD files).
//
// Gives visible progress feedback, disables the submit button during upload,
// and shows inline errors instead of a blocking alert().
//
// Usage: attach `data-controller="psd-upload"` to the <form> tag.
// See _form.html.erb for the full target/action wiring.

export default class extends Controller {
  static targets = [
    "progressContainer", // wrapper div, hidden by default
    "progressBar",       // the inner fill bar
    "progressText",      // "Uploading filename.psd (200 MB) — 42%"
    "errorContainer",    // wrapper div for inline error, hidden by default
    "errorText",         // the error message text
    "submit"             // the submit button
  ]

  connect() {
    this.originalSubmitLabel = this.submitTarget.value

    // Bind to `this` so we can remove them cleanly on disconnect
    this._onInitialize   = this.onInitialize.bind(this)
    this._onProgress     = this.onProgress.bind(this)
    this._onError        = this.onError.bind(this)
    this._onUploadsEnd   = this.onUploadsEnd.bind(this)

    // Events bubble up from the file input to the form, so listening here catches all inputs
    this.element.addEventListener("direct-upload:initialize", this._onInitialize)
    this.element.addEventListener("direct-upload:progress",   this._onProgress)
    this.element.addEventListener("direct-upload:error",      this._onError)
    this.element.addEventListener("direct-uploads:end",       this._onUploadsEnd)
  }

  disconnect() {
    this.element.removeEventListener("direct-upload:initialize", this._onInitialize)
    this.element.removeEventListener("direct-upload:progress",   this._onProgress)
    this.element.removeEventListener("direct-upload:error",      this._onError)
    this.element.removeEventListener("direct-uploads:end",       this._onUploadsEnd)
  }

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  onInitialize(event) {
    const { file } = event.detail
    const sizeMB = (file.size / 1024 / 1024).toFixed(1)

    this.hideError()
    this.showProgress()
    this.setProgressWidth(0)
    this.progressTextTarget.textContent = `Uploading ${file.name} (${sizeMB} MB)…`

    this.submitTarget.disabled = true
    this.submitTarget.value    = "Uploading…"
  }

  onProgress(event) {
    const { progress } = event.detail
    const pct = Math.round(progress)
    this.setProgressWidth(pct)

    // Update the trailing percentage on whatever text is already there
    const base = this.progressTextTarget.textContent.replace(/ — \d+%$/, "")
    this.progressTextTarget.textContent = `${base} — ${pct}%`
  }

  onError(event) {
    // Prevent the default behaviour (which would throw and leave the form frozen)
    event.preventDefault()

    const { error } = event.detail

    this.hideProgress()
    this.showError(
      `Upload failed: ${error}. Please check your connection and try again.`
    )

    // Re-enable the form so the user can retry
    this.submitTarget.disabled = false
    this.submitTarget.value    = this.originalSubmitLabel
  }

  onUploadsEnd(_event) {
    // ActiveStorage will call form.submit() immediately after this event.
    // Update the UI so the user knows we're past the upload phase.
    this.setProgressWidth(100)
    this.progressTextTarget.textContent = "Upload complete — saving…"
    this.submitTarget.value = "Saving…"
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  showProgress() {
    this.progressContainerTarget.classList.remove("hidden")
  }

  hideProgress() {
    this.progressContainerTarget.classList.add("hidden")
  }

  setProgressWidth(pct) {
    this.progressBarTarget.style.width = `${pct}%`
  }

  showError(message) {
    this.errorTextTarget.textContent = message
    this.errorContainerTarget.classList.remove("hidden")
  }

  hideError() {
    this.errorContainerTarget.classList.add("hidden")
  }
}
